import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import posthog from './utils/posthog';

import "./App.css";
import IndexPage from './pages/IndexPage.js';
import MaintenanceBanner from './components/MaintenanceBanner.js';
import MaintenanceGate from './components/MaintenanceGate.js';
import usePreferredLanguageRedirect from './hooks/usePreferredLanguageRedirect.js';
import Modal from './components/components/base/Modal.jsx';
import {
    DEFAULT_LANGUAGE,
    getLanguageCookie,
    resolveLanguageFromPathname,
    setLanguageCookie,
    SUPPORTED_LANGUAGES,
} from './utils/language';
import { homeTranslations } from './i18n/home';
import { languageModalTranslations } from './i18n/languageModals';
import logo from './media/logo.webp';

const LanguageModal = React.lazy(() => import('./components/LanguageModal'));
const LanguageGenderModal = React.lazy(() => import('./components/LanguageGenderModal'));
const LanguageVideoModal = React.lazy(() => import('./components/LanguageVideoModal'));
const ReportIssueButton = React.lazy(() => import('./components/ReportIssueButton.js'));
const FaqComponent = React.lazy(() => import('./components/faqs.js'));
const Terms = React.lazy(() => import('./pages/Terms.js'));
const ReCAPTCHA = React.lazy(() => import('react-google-recaptcha'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy.js'));
const NotFound = React.lazy(() => import('./pages/NotFound.js'));
const LoginPage = React.lazy(() => import('./pages/Login.js'));
const RegisteredSuccess = React.lazy(() => import('./pages/RegisteredSuccess.js'));
const BackendDashboard = React.lazy(() => import('./pages/BackendDashboard.js'));
const Signup = React.lazy(() => import('./components/signup.js'));
const Profile = React.lazy(() => import('./pages/Profile.js'));
const LoginPlease = React.lazy(() => import('./components/loginsignupplease.js'));
const PassResetLinkToBeReceived = React.lazy(() => import('./components/forgottenpassword.js'));

const lazyNamed = (importModule, exportName) => React.lazy(() => importModule().then((module) => ({
    default: module[exportName],
})));

const ForgotPassword = lazyNamed(() => import('./components/signup.js'), 'ForgotPassword');
const Resettingpassword = lazyNamed(() => import('./components/signup.js'), 'Resettingpassword');
const VerificationSuccessful = lazyNamed(() => import('./components/loginsignupplease.js'), 'VerificationSuccessful');
const VerificationFailed = lazyNamed(() => import('./components/loginsignupplease.js'), 'VerificationFailed');
const PassResetSuccessful = lazyNamed(() => import('./components/forgottenpassword.js'), 'PassResetSuccessful');

const INDEX_ROUTES = ['/', '/en', '/sw', '/so'];

function SignupTypeModal({ open, language, onSelect }) {
    const normalizedLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;
    const labels = languageModalTranslations[normalizedLanguage] || languageModalTranslations[DEFAULT_LANGUAGE];
    const buttonStyle = {
        background: 'linear-gradient(to right, #991b1b, #000000) padding-box, linear-gradient(90deg, #f59e0b, #d4af37) border-box',
        border: '2px solid transparent',
    };
    const disabledWaliButtonStyle = {
        ...buttonStyle,
        opacity: 0.45,
        cursor: 'not-allowed',
        filter: 'grayscale(0.4)',
    };

    return (
        <Modal
            open={open}
            overlayClassName="bg-black/95"
            dialogClassName="my-4 rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-red-950 via-black to-amber-900 align-middle sm:my-8 sm:max-w-xl w-full"
            bodyClassName="p-6 sm:p-8"
        >
            <div className="space-y-6 text-center text-white font-league-spartan">
                <header className="space-y-2">
                    <h2 className="text-2xl font-semibold">{labels.signupTypeTitle}</h2>
                    <p className="text-sm text-white/70">{labels.signupTypeDescription}</p>
                </header>

                <div className="grid gap-3 sm:grid-cols-2">
                    <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        className="w-full rounded-lg px-4 py-3 text-xs font-bold text-white transition disabled:pointer-events-none disabled:hover:translate-y-0"
                        style={disabledWaliButtonStyle}
                    >
                        {labels.signupTypeWali}
                    </button>
                    <button
                        type="button"
                        onClick={() => onSelect('individual')}
                        className="w-full rounded-lg px-4 py-3 text-xs font-bold text-white transition hover:-translate-y-0.5"
                        style={buttonStyle}
                    >
                        {labels.signupTypeIndividual}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

function AppRoutes() {
    usePreferredLanguageRedirect();
    const location = useLocation();
    const navigate = useNavigate();
    const isIndexRoute = INDEX_ROUTES.includes(location.pathname);
    const isSignupRoute = location.pathname === '/signup';
    const [showLanguageModal, setShowLanguageModal] = React.useState(false);
    const [showLanguageGenderModal, setShowLanguageGenderModal] = React.useState(false);
    const [showLanguageVideoModal, setShowLanguageVideoModal] = React.useState(false);
    const [showSignupTypeModal, setShowSignupTypeModal] = React.useState(false);
    const [selectedLanguage, setSelectedLanguage] = React.useState(getLanguageCookie());
    const [selectedGender, setSelectedGender] = React.useState(null);
    const [logoReady, setLogoReady] = React.useState(false);
    const [showIntro, setShowIntro] = React.useState(true);

    const introLanguage = React.useMemo(() => {
        const pathLanguage = resolveLanguageFromPathname(location.pathname);
        if (pathLanguage && SUPPORTED_LANGUAGES.includes(pathLanguage)) {
            return pathLanguage;
        }

        const cookieLanguage = getLanguageCookie();
        if (cookieLanguage && SUPPORTED_LANGUAGES.includes(cookieLanguage)) {
            return cookieLanguage;
        }

        return DEFAULT_LANGUAGE;
    }, [location.pathname]);

    const introLabels = homeTranslations[introLanguage] || homeTranslations[DEFAULT_LANGUAGE];
    const flowStep = React.useMemo(() => new URLSearchParams(location.search).get('flow'), [location.search]);
    const isWaliSignup = React.useMemo(() => new URLSearchParams(location.search).get('wali') === 'true', [location.search]);
    const isFromwali = React.useMemo(() => new URLSearchParams(location.search).get('fromwali') === '1', [location.search]);

    const setFlowStep = React.useCallback((step, replace = false) => {
        const params = new URLSearchParams(location.search);
        if (step) {
            params.set('flow', step);
        } else {
            params.delete('flow');
        }

        const search = params.toString();
        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace });
    }, [location.pathname, location.search, navigate]);

    React.useEffect(() => {
        if (!isIndexRoute) {
            setLogoReady(false);
            setShowIntro(false);
            return;
        }

        setLogoReady(false);
        setShowIntro(false);

        let isMounted = true;
        const img = new Image();
        img.src = logo;
        img.onload = () => {
            if (isMounted) {
                setLogoReady(true);
            }
        };
        img.onerror = () => {
            if (isMounted) {
                setLogoReady(true);
            }
        };

        return () => {
            isMounted = false;
            img.onload = null;
            img.onerror = null;
        };
    }, [isIndexRoute, location.pathname]);

    React.useEffect(() => {
        if (!isIndexRoute || !logoReady) {
            return;
        }

        setShowIntro(false);

        return undefined;
    }, [isIndexRoute, logoReady]);

    React.useEffect(() => {
        if (!isIndexRoute) {
            return;
        }

        if (flowStep === 'language') {
            setShowLanguageModal(true);
            setShowLanguageGenderModal(false);
            setShowLanguageVideoModal(false);
            setShowSignupTypeModal(false);
            return;
        }

        if (flowStep === 'gender') {
            setShowLanguageModal(false);
            setShowLanguageGenderModal(true);
            setShowLanguageVideoModal(false);
            setShowSignupTypeModal(false);
            return;
        }

        if (flowStep === 'video') {
            setShowLanguageModal(false);
            setShowLanguageGenderModal(false);
            setShowLanguageVideoModal(true);
            setShowSignupTypeModal(false);
            return;
        }

        if (flowStep === 'signup-type') {
            setShowLanguageModal(false);
            setShowLanguageGenderModal(false);
            setShowLanguageVideoModal(false);
            setShowSignupTypeModal(true);
            return;
        }

        setShowLanguageModal(false);
        setShowLanguageGenderModal(false);
        setShowLanguageVideoModal(false);
        setShowSignupTypeModal(false);
    }, [flowStep, isIndexRoute]);

    const handleLanguageSelect = React.useCallback((code) => {
        if (!SUPPORTED_LANGUAGES.includes(code)) return;
        posthog.capture('language_selected', { language: code });
        setLanguageCookie(code);
        setShowLanguageModal(false);
        setShowSignupTypeModal(false);
        setSelectedLanguage(code);
        setShowLanguageGenderModal(true);
        setShowLanguageVideoModal(false);

        const pathLang = resolveLanguageFromPathname(location.pathname);
        if (!pathLang) {
            navigate(`/${code}?flow=gender`, { replace: location.pathname === '/' });
            return;
        }

        setFlowStep('gender');
    }, [location.pathname, navigate, setFlowStep]);

    const handleGenderSelect = React.useCallback((gender) => {
        posthog.capture('gender_selected', { gender });
        setSelectedGender(gender);
        if (gender) {
            window.localStorage.setItem('preferredGender', gender);
        }
        setShowLanguageGenderModal(false);
        setShowLanguageVideoModal(false);
        setShowSignupTypeModal(false);
        navigate('/signup');
    }, [navigate]);

    const handleBackToLanguage = React.useCallback(() => {
        setShowLanguageGenderModal(false);
        setShowLanguageModal(true);
        setShowSignupTypeModal(false);
        setFlowStep('language');
    }, [setFlowStep]);

    const handleBackToSignupType = React.useCallback(() => {
        setShowLanguageGenderModal(false);
        setShowLanguageVideoModal(false);
        setShowSignupTypeModal(false);
        setShowLanguageModal(true);
        setFlowStep('language');
    }, [setFlowStep]);

    const handleBackToGender = React.useCallback(() => {
        setShowLanguageVideoModal(false);
        setShowLanguageGenderModal(true);
        setShowSignupTypeModal(false);
        setFlowStep('gender');
    }, [setFlowStep]);

    const handleCloseLanguageVideo = React.useCallback(() => {
        setShowLanguageVideoModal(false);
        if (isFromwali) {
            return;
        }
        setShowSignupTypeModal(false);
        navigate('/signup');
    }, [navigate, isFromwali]);

    React.useEffect(() => {
        if (isSignupRoute && isFromwali) {
            setShowLanguageVideoModal(true);
        }
    }, [isSignupRoute, isFromwali]);

    const handleSignupTypeSelect = React.useCallback((type) => {
        posthog.capture('signup_type_selected', { signup_type: type });
        setShowSignupTypeModal(false);
        if (type === 'wali') {
            navigate('/signup?wali=true');
            return;
        }
        setShowLanguageGenderModal(true);
        setFlowStep('gender');
    }, [navigate, setFlowStep]);

    if (isIndexRoute && showIntro) {
        return (
            <div className="intro-overlay">
                <div className="intro-content">
                    <img className="intro-logo" src={logo} alt="Perfect Timing logo acc" />
                    <p className="intro-text">{introLabels.introGreeting}</p>
                </div>
            </div>
        );
    }

    return (
        <MaintenanceGate>
            {showLanguageModal && (
                <React.Suspense fallback={null}>
                    <LanguageModal open={showLanguageModal} onSelectLanguage={handleLanguageSelect} />
                </React.Suspense>
            )}
            
            {showLanguageGenderModal && (
                <React.Suspense fallback={null}>
                    <LanguageGenderModal
                        open={showLanguageGenderModal}
                        language={selectedLanguage}
                        onSelectGender={handleGenderSelect}
                        onBack={handleBackToSignupType}
                    />
                </React.Suspense>
            )}
            
            {showLanguageVideoModal && (
                <React.Suspense fallback={null}>
                    <LanguageVideoModal
                        open={showLanguageVideoModal}
                        language={selectedLanguage}
                        onClose={handleCloseLanguageVideo}
                        onBack={handleBackToGender}
                    />
                </React.Suspense>
            )}

            <SignupTypeModal
                open={showSignupTypeModal}
                language={selectedLanguage}
                onSelect={handleSignupTypeSelect}
            />

            {!isIndexRoute && !isSignupRoute && (
                <React.Suspense fallback={null}>
                    <ReportIssueButton />
                </React.Suspense>
            )}
            <MaintenanceBanner />
            <React.Suspense fallback={<div className="p-6 text-center text-sm text-gray-600">Loading...</div>}>
                <Routes>
                    <Route path="/" element={<IndexPage />} />
                    <Route path="/en" element={<IndexPage />} />
                    <Route path="/sw" element={<IndexPage />} />
                    <Route path="/so" element={<IndexPage />} />

                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/recaptcha" element={<ReCAPTCHA sitekey={process.env.REACT_APP_SITE_KEY} />} />
                    <Route path="/forgottenpassword" element={<ForgotPassword url="/api/forgottenpassword" />} />
                    <Route path="/resetpassword/:emailid" element={<Resettingpassword url="/api/resettingpassword" />} />
                    <Route path="/resetpasswordsuccess" element={<PassResetSuccessful />} />
                    <Route path="/forgottenpasswordsubmitted" element={<PassResetLinkToBeReceived />} />
                    <Route path="/forgottenpassword" element={<ForgotPassword url="/api/forgottenpassword" />} />

                    <Route path="/faq" element={<FaqComponent />} />
                    <Route path="/loginplease" element={<LoginPlease />} />

                    <Route path="/signup" element={
                      <div className="signup-page relative min-h-screen w-full">
                        <Signup url="/api/store_data" allrequired={true} waliSignup={isWaliSignup} />
                      </div>
                    } />

                    <Route path="/profile" element={<Profile />} />
                    <Route path="/profile/edit" element={<Profile />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                    <Route path="/backend/*" element={<BackendDashboard />} />
                    
                    <Route path="/verificationsuccessful" element={<VerificationSuccessful />} />
                    <Route path="/verificationfailed" element={<VerificationFailed />} />
                    <Route path="/registrationsuccess" element={<RegisteredSuccess />} />
                    <Route path="/finishedsignup" element={<RegisteredSuccess />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </React.Suspense>
        </MaintenanceGate>
    );
}

function App() {
    return (
        <div className="uppercase">
            <Router>
                <AppRoutes />
            </Router>
        </div>
    );
}

export default App;
